/* bacter-residuos.js
   Calculadora de dosis para Residuos y Cuartos de basura
   (Edificios, plazas, supermercados, hoteles, mercados, hospitales)

   - Puntos:
       · Cuartos de basura / manejo de residuos
       · Contenedores, compactadores, tolvas
       · Cámaras frías de residuos
       · Drenajes / coladeras de lixiviados
   - Dosis base (carga media, mantenimiento):
       · Cuarto de basura:       120 g/mes
       · Contenedor/compactador: 150 g/mes
       · Cámara fría residuos:    90 g/mes
       · Drenaje lixiviados:      60 g/mes
   - Ajustes:
       · Carga orgánica: baja 0.8 · media 1.0 · alta 1.5
       · Modo: mantenimiento 1.0 · choque 2.0
       · Químicos fuertes: ×1.5
   - Polvo vs solución:
       · 70 % de la dosis de contenedores en polvo directo (sin diluir)
       · 30 % de la dosis de contenedores + cuartos + cámaras + drenajes en solución
   - Dilución escalada para la parte que va en solución (más concentrada):
       · ≤ 150 g      → 30 g/L
       · 150–1500 g   → 100 g/L
       · > 1500 g     → 150 g/L
   - Volumen en litros, redondeado a múltiplos de 0.5 L
*/

(function () {
  'use strict';

  // Dosis base por punto (g/mes, carga media, mantenimiento)
  var BASE_CUARTO     = 120;
  var BASE_CONTENEDOR = 150;
  var BASE_CAMARA     = 90;
  var BASE_DRENAJE    = 60;

  var CARGA_MULT = { baja: 0.8, media: 1.0, alta: 1.5 };
  var MODO_MULT  = { mantenimiento: 1.0, choque: 2.0 };

  // -------- utilerías --------

  function qsa(selector, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(selector));
  }

  function el(tag, attrs) {
    var e = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        e.setAttribute(k, attrs[k]);
      });
    }
    return e;
  }

  // Formatea gramos a "xxx g" o "x.x kg (xxxx g)"
  function formatGramos(gramos, opciones) {
    opciones = opciones || {};
    var incluirParentesis = opciones.mostrarParentesis !== false; // por defecto true
    var g = Math.round(Number(gramos) || 0);
    if (g <= 0) return '0 g';

    if (g >= 1000) {
      var kg = g / 1000;
      var kgStr = kg.toFixed(1).replace(/\.0$/, '');
      var txt = kgStr + ' kg';
      if (incluirParentesis) {
        txt += ' (' + g.toLocaleString('es-MX') + ' g)';
      }
      return txt;
    } else {
      return g.toLocaleString('es-MX') + ' g';
    }
  }

  // Litros según escala de concentración, para la parte que va en solución
  // (más concentrada que hogar, para no pedir tanta agua)
  function calcularLitrosEscalado(gramosSolucion) {
    var g = Number(gramosSolucion) || 0;
    if (g <= 0) return 0;

    var ratio; // g por litro

    if (g <= 150) {
      // Pocas dosis → solución concentrada pero manejable
      ratio = 30;          // 30 g/L
    } else if (g <= 1500) {
      // Rango típico de operación en estos sitios
      ratio = 100;         // 100 g/L
    } else {
      // Volúmenes muy altos → solución muy concentrada
      ratio = 150;         // 150 g/L
    }

    return g / ratio;
  }

  function redondearLitros05(litrosCrudos) {
    var L = Number(litrosCrudos) || 0;
    if (L <= 0) return 0;
    var paso = 0.5;
    var steps = L / paso;
    var low   = Math.floor(steps) * paso;
    var high  = Math.ceil(steps) * paso;
    var distLow  = Math.abs(L - low);
    var distHigh = Math.abs(L - high);
    var res = (distHigh < distLow) ? high : low;
    if (res <= 0) res = paso;
    return res;
  }

  function mlToLitStr(ml) {
    if (!ml || ml <= 0) return '';
    var litros = ml / 1000;
    var txt = litros.toFixed(1);
    txt = txt.replace(/\.0$/, '');
    return txt + ' L';
  }

  // -------- render del widget --------

  function renderWidget(container, cfg) {
    container.innerHTML = '';

    var title = cfg.title || '🔢 Calcula aquí la dosis recomendada — Bacter spp.';
    var h = el('div');
    h.className = 'bacter-title';
    h.style.fontWeight = '800';
    h.style.marginBottom = '8px';
    h.style.fontSize = '24px';
    h.style.textAlign = 'center';
    h.innerText = title;
    container.appendChild(h);

    var form = el('div');
    form.style.display = 'flex';
    form.style.flexWrap = 'wrap';
    form.style.gap = '8px';
    form.style.marginBottom = '10px';

    function labeledNumber(id, label, value, extraConfig) {
      var wrap = el('label');
      wrap.style.flex = '1 1 220px';
      wrap.style.fontSize = '13px';
      wrap.style.display = 'block';

      var lbl = el('div');
      lbl.style.fontWeight = '600';
      lbl.style.marginBottom = '6px';
      lbl.innerText = label;

      var input = el('input');
      input.type = 'number';
      input.value = (value !== undefined ? value : 0);
      input.min = 0;
      input.style.width = '100%';
      input.style.padding = '8px';
      input.style.border = '1px solid #dcdcdc';
      input.style.borderRadius = '6px';
      input.setAttribute('data-role', id);

      if (extraConfig && extraConfig.readonly) {
        input.readOnly = true;
        input.style.backgroundColor = '#f3f3f3';
      }

      wrap.appendChild(lbl);
      wrap.appendChild(input);
      return wrap;
    }

    // Campos principales
    form.appendChild(
      labeledNumber(
        'cuartos_basura',
        'Nº Cuartos de basura\no de manejo de residuos',
        cfg.defaults.cuartos_basura
      )
    );

    form.appendChild(
      labeledNumber(
        'contenedores',
        'Nº Contenedores, compactadores\no tolvas',
        cfg.defaults.contenedores
      )
    );

    form.appendChild(
      labeledNumber(
        'camaras_frias',
        'Nº Cámaras frías\nde residuos',
        cfg.defaults.camaras_frias
      )
    );

    form.appendChild(
      labeledNumber(
        'drenajes_lixiviados',
        'Nº Drenajes / coladeras\nen zona de residuos',
        cfg.defaults.drenajes_lixiviados
      )
    );

    // Carga orgánica
    var cargaDiv = el('div');
    cargaDiv.style.flex = '1 1 200px';
    var lblCarga = el('div');
    lblCarga.style.fontWeight = '600';
    lblCarga.style.marginBottom = '6px';
    lblCarga.innerText = 'Carga orgánica\ngeneral';

    var selCarga = el('select');
    selCarga.setAttribute('data-role', 'carga');
    selCarga.style.padding = '8px';
    selCarga.style.border = '1px solid #dcdcdc';

    ['baja', 'media', 'alta'].forEach(function (v) {
      var o = el('option');
      o.value = v;
      o.innerText =
        v === 'baja'
          ? 'Baja (poca carga de residuos)'
          : v === 'media'
          ? 'Media (volumen normal de residuos)'
          : 'Alta (gran volumen o residuos muy cargados)';
      if (v === cfg.defaults.carga) o.selected = true;
      selCarga.appendChild(o);
    });

    cargaDiv.appendChild(lblCarga);
    cargaDiv.appendChild(selCarga);
    form.appendChild(cargaDiv);

    // Modo
    var modoDiv = el('div');
    modoDiv.style.flex = '1 1 200px';
    var lblModo = el('div');
    lblModo.style.fontWeight = '600';
    lblModo.style.marginBottom = '6px';
    lblModo.innerText = 'Modo de\naplicación';

    var selModo = el('select');
    selModo.setAttribute('data-role', 'modo');
    selModo.style.padding = '8px';
    selModo.style.border = '1px solid #dcdcdc';

    ['mantenimiento', 'choque'].forEach(function (v) {
      var o = el('option');
      o.value = v;
      o.innerText =
        v === 'mantenimiento'
          ? 'Mantenimiento (uso regular del sitio)'
          : 'Choque (problemas fuertes de olor o carga muy alta)';
      if (v === cfg.defaults.modo) o.selected = true;
      selModo.appendChild(o);
    });

    modoDiv.appendChild(lblModo);
    modoDiv.appendChild(selModo);
    form.appendChild(modoDiv);

    // Químicos fuertes
    var qDiv = el('div');
    qDiv.style.flex = '1 1 260px';
    qDiv.style.display = 'flex';
    qDiv.style.alignItems = 'flex-end';

    var chk = el('input');
    chk.type = 'checkbox';
    chk.setAttribute('data-role', 'quimicos');
    chk.checked = cfg.defaults.quimicos;

    var chkLbl = el('label');
    chkLbl.style.marginLeft = '8px';
    chkLbl.style.fontSize = '13px';
    chkLbl.innerText =
      '¿Se usan cloro, sosa o desengrasantes fuertes con frecuencia en la zona de residuos?';

    qDiv.appendChild(chk);
    qDiv.appendChild(chkLbl);
    form.appendChild(qDiv);

    container.appendChild(form);

    // Resultado
    var res = el('div');
    res.setAttribute('data-role', 'resultado');
    res.style.marginTop = '12px';
    res.style.padding = '12px';
    res.style.background = '#f8f9fa';
    res.style.borderRadius = '6px';
    res.style.display = 'none';
    container.appendChild(res);

    // Eventos
    qsa('input[type="number"]', container).forEach(function (inp) {
      inp.addEventListener('input', function () {
        doCalc(container, cfg);
      });
    });

    qsa('select', container).forEach(function (sel) {
      sel.addEventListener('change', function () {
        doCalc(container, cfg);
      });
    });

    var qNode = container.querySelector('[data-role="quimicos"]');
    if (qNode) {
      qNode.addEventListener('change', function () {
        doCalc(container, cfg);
      });
    }

    doCalc(container, cfg);
  }

  // -------- lógica de cálculo --------

  function doCalc(container, cfg) {
    function getVal(sel, asInt) {
      var node = container.querySelector('[data-role="' + sel + '"]');
      if (!node) return 0;
      if (node.type === 'checkbox') return node.checked;
      var v = node.value;
      if (asInt) return parseInt(v || 0, 10) || 0;
      return v || 0;
    }

    var cuartos      = getVal('cuartos_basura', true);
    var contenedores = getVal('contenedores', true);
    var camaras      = getVal('camaras_frias', true);
    var drenajes     = getVal('drenajes_lixiviados', true);

    if (cuartos < 0) cuartos = 0;
    if (contenedores < 0) contenedores = 0;
    if (camaras < 0) camaras = 0;
    if (drenajes < 0) drenajes = 0;

    var carga = getVal('carga') || cfg.defaults.carga || 'media';
    var modo  = getVal('modo')  || cfg.defaults.modo  || 'mantenimiento';

    var quimicos = container.querySelector('[data-role="quimicos"]')
      ? container.querySelector('[data-role="quimicos"]').checked
      : cfg.defaults.quimicos;

    var multCarga = CARGA_MULT[carga] || 1.0;
    var multModo  = MODO_MULT[modo]   || 1.0;
    var multQuim  = quimicos ? 1.5 : 1.0;

    var factorGlobal = multCarga * multModo * multQuim;

    // Dosis base por tipo de punto
    var baseCuartos  = cuartos      * BASE_CUARTO;
    var baseCont     = contenedores * BASE_CONTENEDOR;
    var baseCamaras  = camaras      * BASE_CAMARA;
    var baseDrenajes = drenajes     * BASE_DRENAJE;

    // Dosis ajustada por carga / modo / químicos
    var dosisCuartos  = Math.round(baseCuartos  * factorGlobal);
    var dosisCont     = Math.round(baseCont     * factorGlobal);
    var dosisCamaras  = Math.round(baseCamaras  * factorGlobal);
    var dosisDrenajes = Math.round(baseDrenajes * factorGlobal);

    var dosisTotal = dosisCuartos + dosisCont + dosisCamaras + dosisDrenajes;

    // Mínimo razonable para este sector (si hay al menos un punto)
    var puntosTotales = cuartos + contenedores + camaras + drenajes;
    if (puntosTotales > 0 && dosisTotal < 60) {
      dosisTotal = 60;
    }

    // Reajustar proporcionalmente si se elevó al mínimo
    var sumaOriginal = dosisCuartos + dosisCont + dosisCamaras + dosisDrenajes;
    if (sumaOriginal > 0 && dosisTotal !== sumaOriginal) {
      var factorMin = dosisTotal / sumaOriginal;
      dosisCuartos  = Math.round(dosisCuartos  * factorMin);
      dosisCont     = Math.round(dosisCont     * factorMin);
      dosisCamaras  = Math.round(dosisCamaras  * factorMin);
      dosisDrenajes = Math.round(dosisDrenajes * factorMin);
      dosisTotal    = dosisCuartos + dosisCont + dosisCamaras + dosisDrenajes;
    }

    // Separación polvo vs solución:
    // 70% de la dosis de contenedores en polvo directo (sin diluir)
    var gramosPolvoCont = Math.round(dosisCont * 0.7);
    var gramosContSol   = dosisCont - gramosPolvoCont;

    var gramosSolucion =
      dosisCuartos + dosisCamaras + dosisDrenajes + gramosContSol;

    // Dilución solo para la parte que va en solución (ya con la nueva escala concentrada)
    var litrosCrudos      = calcularLitrosEscalado(gramosSolucion);
    var litrosRedondeados = redondearLitros05(litrosCrudos);
    var totalAguaMl       = Math.round(litrosRedondeados * 1000);

    // Reparto del volumen de solución entre zonas
    var gramosSolTot = gramosSolucion > 0 ? gramosSolucion : 0;

    var gramosInt           = dosisCuartos + dosisCamaras; // interiores de cuartos + cámaras
    var gramosDren          = dosisDrenajes;
    var gramosContSolLocal  = gramosContSol;

    var mlInt   = 0;
    var mlDren  = 0;
    var mlContS = 0;

    if (gramosSolTot > 0 && totalAguaMl > 0) {
      mlInt   = Math.round(totalAguaMl * gramosInt          / gramosSolTot);
      mlDren  = Math.round(totalAguaMl * gramosDren         / gramosSolTot);
      mlContS = Math.round(totalAguaMl * gramosContSolLocal / gramosSolTot);
    }

    // -------- armado del resultado --------

    var resDiv = container.querySelector('[data-role="resultado"]');
    if (!resDiv) return;

    if (puntosTotales <= 0) {
      resDiv.innerHTML =
        '<div style="font-size:15px;color:#666;">' +
        'Indica al menos un cuarto de basura, contenedor, cámara o drenaje para calcular la dosis recomendada.' +
        '</div>';
      resDiv.style.display = 'block';
      return;
    }

    var html = '';

    // Encabezado dosis total
    html += '<div style="font-size:18px;">' +
            'Dosis recomendada por mes para el sitio ' +
            '(toda la zona de residuos y basura): ' +
            '<span style="font-size:24px;font-weight:900;color:#0b3d91;">' +
            formatGramos(dosisTotal) +
            '</span>' +
            '</div>';

    // Explicación general de cómo se aplica
    if (litrosRedondeados > 0) {
      var litTotalTxt = litrosRedondeados.toFixed(1).replace(/\.0$/, '');

      html += '<div style="margin-top:8px;font-size:15px;color:#0b3d91;">' +
              'Esta dosis mensual se aplica en dos formas: ' +
              '<strong>una parte en solución concentrada</strong> para rociar pisos, paredes y drenajes, ' +
              'y <strong>otra parte directamente en polvo</strong> sobre los residuos en contenedores y compactadores. ' +
              'Para la parte que va en solución, diluye aproximadamente <strong>' +
              formatGramos(gramosSolucion, { mostrarParentesis: false }) +
              '</strong> en <strong>' + litTotalTxt +
              ' litros</strong> de agua a temperatura ambiente.' +
              '</div>';
    }

    // Referencias rápidas
    var dosisPromPunto = puntosTotales > 0 ? Math.round(dosisTotal / puntosTotales) : 0;

    html += '<div style="margin-top:10px;font-size:15px;color:#333;">' +
            '<strong>Referencias rápidas:</strong> ' +
            'Puntos de aplicación: <strong>' + puntosTotales + '</strong> · ' +
            'Dosis promedio por punto: <strong>' + dosisPromPunto + ' g/mes</strong> aprox.' +
            '</div>';

    html += '<div style="margin-top:10px;font-size:16px;"><strong>Cómo repartirla en tu zona de residuos:</strong></div>';

    // Interiores de cuartos de basura y cámaras de residuos
    if (gramosInt > 0) {
      html += '<div style="margin-top:6px;font-size:15px;">' +
              '<strong>Interiores de cuartos de basura y cámaras de residuos</strong> ' +
              '(pisos, paredes, puertas y coladeras internas): ' +
              '<strong>' + formatGramos(gramosInt) +
              '</strong> en total para todos los cuartos y cámaras';

      if (mlInt > 0) {
        html += ', diluidos en ≈ <strong>' + mlToLitStr(mlInt) +
                '</strong> de la solución para rociar estas áreas.';
      } else {
        html += '.';
      }
      html += '</div>';
    }

    // Drenajes y coladeras de lixiviados
    if (gramosDren > 0) {
      html += '<div style="margin-top:4px;font-size:15px;">' +
              '<strong>Drenajes y coladeras de lixiviados</strong> ' +
              '(coladeras en cuartos de basura, fosas de escurrimientos): ' +
              '<strong>' + formatGramos(gramosDren) +
              '</strong> en total para todos estos puntos';

      if (mlDren > 0) {
        html += ', diluidos en ≈ <strong>' + mlToLitStr(mlDren) +
                '</strong> de la solución para verter directamente en cada drenaje o coladera.';
      } else {
        html += '.';
      }
      html += '</div>';
    }

    // Contenedores / compactadores / tolvas
    if (dosisCont > 0) {
      html += '<div style="margin-top:4px;font-size:15px;">' +
              '<strong>Contenedores, compactadores y tolvas</strong>: ' +
              '<strong>' + formatGramos(dosisCont) +
              '</strong> en total para todos los equipos. ' +
              'Se recomienda dividir esta dosis en dos partes:' +
              '</div>';

      // Polvo directo
      html += '<div style="margin-left:1.2rem;margin-top:2px;font-size:14px;">' +
              '• <strong>Aplicación directa en polvo (sin diluir):</strong> ' +
              '<strong>' + formatGramos(gramosPolvoCont, { mostrarParentesis: false }) +
              '</strong> espolvoreados directamente sobre los residuos frescos, ' +
              'zonas de escurrimiento y paredes internas al momento de recibir la carga.' +
              '</div>';

      // Parte diluida para lavado
      if (gramosContSolLocal > 0) {
        html += '<div style="margin-left:1.2rem;margin-top:2px;font-size:14px;">' +
                '• <strong>Aplicación en solución concentrada:</strong> ' +
                '<strong>' + formatGramos(gramosContSolLocal, { mostrarParentesis: false }) +
                '</strong> diluidos en ≈ <strong>' + mlToLitStr(mlContS) +
                '</strong> de la solución para rociar paredes, tapas, marcos y zonas de goteo de los contenedores.' +
                '</div>';
      }
    }

    // Cierre técnico
    html += '<div style="margin-top:12px;color:#444;font-size:15px;">' +
            'Sugerencia: aplica preferentemente al final de la jornada o en horarios de poco movimiento de residuos. ' +
            'En compactadores y contenedores cerrados, es conveniente aplicar el polvo directamente ' +
            'sobre la carga fresca antes de cerrar el equipo, y usar la solución concentrada para lavado de paredes y pisos circundantes.' +
            '</div>';

    html += '<div style="margin-top:8px;font-size:14px;color:#666;">' +
            '<em>Nota técnica:</em> pH recomendado 5–9; temperatura ideal ~28 °C. ' +
            'Ajusta dosis y frecuencia según la generación real de residuos, olores y resultados en campo.' +
            '</div>';

    resDiv.innerHTML = html;
    resDiv.style.display = 'block';
  }

  // -------- init --------

  function init() {
    // data-scenario específico para este módulo
    var widgets = qsa('.bacter-widget[data-scenario="residuos_cuartos_basura"]');
    if (!widgets.length) return;

    widgets.forEach(function (elWidget) {
      try {
        var cfg = {
          scenario: elWidget.getAttribute('data-scenario') || 'residuos_cuartos_basura',
          title: elWidget.getAttribute('data-title') || null,
          defaults: {
            cuartos_basura:      parseInt(elWidget.getAttribute('data-default-cuartos-basura')      || 1, 10),
            contenedores:        parseInt(elWidget.getAttribute('data-default-contenedores')        || 1, 10),
            camaras_frias:       parseInt(elWidget.getAttribute('data-default-camaras-frias')       || 0, 10),
            drenajes_lixiviados: parseInt(elWidget.getAttribute('data-default-drenajes-lixiviados') || 2, 10),
            carga:   elWidget.getAttribute('data-default-carga') || 'media',
            modo:    elWidget.getAttribute('data-default-modo')  || 'mantenimiento',
            quimicos: (elWidget.getAttribute('data-default-quimicos') === 'true')
          }
        };

        renderWidget(elWidget, cfg);
      } catch (err) {
        console.error('bacter-residuos init error', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
